<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar viaje" : "Nuevo viaje" }}
      </h1>
    </div>
    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>

        <b-card tag="article" no-body class="custom-card" style="max-width: 900px">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>{{ editing ? "Datos del viaje" : "Datos del nuevo viaje" }}</h3>
          </b-card-header>
          <b-card-body class="custom-card-body p-0">
            <section class="form-section">

              <!-- ═══════════ SECCIÓN 1: ¿Cómo llega la reserva? ═══════════ -->
              <div>
                <h2 class="form-subtitle">¿Cómo llega la reserva?</h2>
              </div>
              <div class="row">

                <!-- a) Botones de tipo de fuente -->
                <div class="col-md-12">
                  <div class="form-group">
                    <label>Tipo de fuente *</label>
                    <div class="source-type-group">
                      <button type="button" class="source-btn"
                        :class="{ active: form.source_type === 'DIRECT' }"
                        @click="selectSourceType('DIRECT')">Directo</button>
                      <button type="button" class="source-btn"
                        :class="{ active: form.source_type === 'OTA' }"
                        @click="selectSourceType('OTA')">OTA</button>
                      <button type="button" class="source-btn"
                        :class="{ active: form.source_type === 'AFFILIATE' }"
                        @click="selectSourceType('AFFILIATE')">Afiliado</button>
                    </div>
                    <FormError v-if="$v.form.fuente.$error && !$v.form.fuente.required"
                      message="La fuente es requerida" />
                  </div>
                </div>

                <!-- b) Sub-fuente condicional: OTA -->
                <div class="col-md-4" v-if="form.source_type === 'OTA'">
                  <div class="form-group">
                    <label for="ota_id">OTA *</label>
                    <select id="ota_id" v-model="form.ota_id" class="form-select">
                      <option :value="null">Elegir OTA</option>
                      <option v-for="o in bookingSources.otas" :key="'ota-'+o.id" :value="o.id">{{ o.label }}</option>
                    </select>
                  </div>
                </div>

                <!-- b) Sub-fuente condicional: Afiliado -->
                <div class="col-md-4" v-if="form.source_type === 'AFFILIATE'">
                  <div class="form-group">
                    <label for="affiliate_id">Afiliado *</label>
                    <select id="affiliate_id" v-model="form.affiliate_id" class="form-select">
                      <option :value="null">Elegir afiliado</option>
                      <option v-for="a in bookingSources.affiliates" :key="'aff-'+a.id" :value="a.id">{{ a.label }}</option>
                    </select>
                  </div>
                </div>

                <!-- c) Canal -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="channel">Canal</label>
                    <FormInput v-if="form.source_type === 'OTA'" id="channel" type="text" value="Plataforma" disabled />
                    <FormSelect v-else id="channel" v-model="form.channel" :items="channelOptions"
                      defaultOption="Elegir canal" />
                  </div>
                </div>

                <!-- d) Banner informativo -->
                <div class="col-md-12">
                  <div v-if="form.source_type === 'OTA'" class="source-banner source-banner--ota">
                    Modo OTA: precio manual, moneda USD
                  </div>
                  <div v-else class="source-banner source-banner--auto">
                    Precio automático según lista
                  </div>
                </div>

              </div>

              <!-- ═══════════ SECCIÓN 2: Datos del pasajero ═══════════ -->
              <div>
                <h2 class="form-subtitle">Datos del pasajero</h2>
              </div>
              <div class="row">

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="celular">Celular *</label>
                    <div class="phone-input-group">
                      <FormInput id="celular" type="text" v-model.trim="form.celular" autocomplete="off"
                        placeholder="Ingresar celular (solo números)"
                        :show-error="$v.form.celular.$error && !$v.form.celular.required"
                        @input="form.celular = $event.replace(/[^0-9]/g, '')" />
                      <button type="button" class="button button-light phone-search-btn"
                        @click="searchPassenger(form.celular)" :disabled="searchingPhone || !form.celular">
                        Buscar
                      </button>
                    </div>
                    <FormError v-if="$v.form.celular.$error && !$v.form.celular.required"
                      message="El celular es requerido" />
                    <span v-if="searchingPhone" class="field-hint">Buscando pasajero...</span>
                    <span v-if="passengerFound && passengerDetected" class="field-hint field-hint--success">Pasajero registrado: {{ passengerDetected.full_name }}</span>
                    <span v-if="phoneSearched && !passengerFound && !bookingHistory && !searchingPhone" class="field-hint field-hint--muted">No se encontró pasajero registrado</span>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="nombres">Nombres *</label>
                    <FormInput id="nombres" type="text" v-model.trim="form.nombres" autocomplete="off"
                      placeholder="Ingresar nombres"
                      :show-error="$v.form.nombres.$error && !$v.form.nombres.required" />
                    <FormError v-if="$v.form.nombres.$error && !$v.form.nombres.required"
                      message="Los nombres son requeridos" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="apellidos">Apellidos *</label>
                    <FormInput id="apellidos" type="text" v-model.trim="form.apellidos" autocomplete="off"
                      placeholder="Ingresar apellidos"
                      :show-error="$v.form.apellidos.$error && !$v.form.apellidos.required" />
                    <FormError v-if="$v.form.apellidos.$error && !$v.form.apellidos.required"
                      message="Los apellidos son requeridos" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="country_code">País (detectado)</label>
                    <FormInput id="country_code" type="text" :value="form.country_code" disabled
                      placeholder="Se detecta al buscar" />
                  </div>
                </div>

                <!-- El segmento solo describe a un cliente directo: para OTA y
                     afiliados el backend lo fija en Regular, asi que pedirlo
                     seria pedir un dato que se descarta (DEF-ADM-027). -->
                <div v-if="isDirectFuente" class="col-md-4">
                  <div class="form-group">
                    <label for="tipo_pasajero">Segmento *</label>
                    <FormInput v-if="passengerDetected" id="tipo_pasajero" type="text"
                      :value="form.tipo_pasajero" disabled />
                    <FormSelect v-else id="tipo_pasajero" v-model="form.tipo_pasajero" :items="tipoPasajeroOptions"
                      defaultOption="Elegir segmento"
                      :showError="$v.form.tipo_pasajero.$error && !$v.form.tipo_pasajero.required" />
                    <FormError v-if="$v.form.tipo_pasajero.$error && !$v.form.tipo_pasajero.required"
                      message="El segmento es requerido" />
                    <span v-if="passengerTypeDetected" class="field-hint field-hint--success">Detectado: {{ passengerTypeLabel }}</span>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="cant_pax">Cant. pasajeros *</label>
                    <FormInput id="cant_pax" type="number" min="1" v-model.number="form.cant_pax" autocomplete="off"
                      :show-error="$v.form.cant_pax.$error && !$v.form.cant_pax.required" />
                    <FormError v-if="$v.form.cant_pax.$error && !$v.form.cant_pax.required"
                      message="La cantidad de pasajeros es requerida" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="cant_maletas">Cant. maletas *</label>
                    <FormInput id="cant_maletas" type="number" min="0" v-model.number="form.cant_maletas"
                      autocomplete="off"
                      :show-error="$v.form.cant_maletas.$error && !$v.form.cant_maletas.required" />
                    <FormError v-if="$v.form.cant_maletas.$error && !$v.form.cant_maletas.required"
                      message="La cantidad de maletas es requerida" />
                  </div>
                </div>

                <!-- Alerta de historial (pasajero no registrado con viajes previos) -->
                <div class="col-md-12" v-if="bookingHistory && !passengerFound">
                  <div class="source-banner source-banner--history">
                    Este pasajero ha viajado {{ bookingHistory.count }}
                    {{ bookingHistory.count === 1 ? 'vez' : 'veces' }} antes
                    (última fuente: {{ bookingHistory.last_source || '—' }}, fecha: {{ bookingHistory.last_date || '—' }}).
                    ¿Registrarlo?
                  </div>
                </div>

                <div class="col-md-12" v-if="!passengerFound">
                  <div class="form-group">
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.save_passenger" />
                      Registrar como pasajero
                    </label>
                  </div>
                </div>

              </div>

              <!-- ═══════════ SECCIÓN 3: Datos del viaje (ruta) ═══════════ -->
              <div>
                <h2 class="form-subtitle">Datos del viaje</h2>
              </div>
              <div class="row">

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="tipo_viaje">Tipo de viaje *</label>
                    <FormSelect id="tipo_viaje" v-model="form.tipo_viaje" :items="tipoViajeOptions"
                      defaultOption="Elegir tipo de viaje"
                      :showError="$v.form.tipo_viaje.$error && !$v.form.tipo_viaje.required" />
                    <FormError v-if="$v.form.tipo_viaje.$error && !$v.form.tipo_viaje.required"
                      message="El tipo de viaje es requerido" />
                  </div>
                </div>

                <!-- Origen -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="origen">Origen *</label>
                    <FormInput v-if="origenReadonly" id="origen" type="text" :value="form.origen" disabled />
                    <FormInput v-else id="origen" type="text" v-model.trim="form.origen" autocomplete="off"
                      placeholder="Ingresar origen"
                      :show-error="$v.form.origen.$error && !$v.form.origen.required" />
                    <FormError v-if="!origenReadonly && $v.form.origen.$error && !$v.form.origen.required"
                      message="El origen es requerido" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="distrito_origen">Distrito origen</label>
                    <FormSelect v-if="!distritoOrigenReadonly" id="distrito_origen" v-model="form.distrito_origen"
                      :items="districtOptions" defaultOption="Elegir distrito" />
                    <FormInput v-else id="distrito_origen" type="text" :value="form.distrito_origen" disabled />
                  </div>
                </div>

                <!-- Destino -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="destino">Destino *</label>
                    <FormInput v-if="destinoReadonly" id="destino" type="text" :value="form.destino" disabled />
                    <FormInput v-else id="destino" type="text" v-model.trim="form.destino" autocomplete="off"
                      placeholder="Ingresar destino"
                      :show-error="$v.form.destino.$error && !$v.form.destino.required" />
                    <FormError v-if="!destinoReadonly && $v.form.destino.$error && !$v.form.destino.required"
                      message="El destino es requerido" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="distrito_destino">Distrito destino</label>
                    <FormSelect v-if="!distritoDestinoReadonly" id="distrito_destino" v-model="form.distrito_destino"
                      :items="districtOptions" defaultOption="Elegir distrito" />
                    <FormInput v-else id="distrito_destino" type="text" :value="form.distrito_destino" disabled />
                  </div>
                </div>

              </div>

              <!-- ═══════════ SECCIÓN 4: Servicio y precio ═══════════ -->
              <div>
                <h2 class="form-subtitle">Servicio y precio</h2>
              </div>
              <div class="row">

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="tipo_vehiculo">Tipo de vehiculo *</label>
                    <FormSelect id="tipo_vehiculo" v-model="form.tipo_vehiculo" :items="tipoVehiculoOptions"
                      defaultOption="Elegir tipo de vehiculo"
                      :showError="$v.form.tipo_vehiculo.$error && !$v.form.tipo_vehiculo.required" />
                    <FormError v-if="$v.form.tipo_vehiculo.$error && !$v.form.tipo_vehiculo.required"
                      message="El tipo de vehiculo es requerido" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="moneda">Moneda *</label>
                    <FormSelect id="moneda" v-model="form.moneda" :items="monedaOptions"
                      defaultOption="Elegir moneda"
                      :showError="$v.form.moneda.$error && !$v.form.moneda.required" />
                    <FormError v-if="$v.form.moneda.$error && !$v.form.moneda.required"
                      message="La moneda es requerida" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="precio">Precio *</label>
                    <FormInput id="precio" type="number" min="0" step="0.01" v-model.number="form.precio"
                      autocomplete="off"
                      :show-error="$v.form.precio.$error && !$v.form.precio.required" />
                    <FormError v-if="$v.form.precio.$error && !$v.form.precio.required"
                      message="El precio es requerido" />
                    <span v-if="calculatingPrice" class="field-hint">Calculando precio (editable)...</span>
                  </div>
                </div>

                <!-- Afiliado con múltiples listas: tarjetas seleccionables -->
                <div class="col-md-12" v-if="availablePriceTypes.length > 1">
                  <div class="form-group">
                    <label>Lista de precios del afiliado</label>
                    <div class="price-cards">
                      <button type="button" v-for="opt in availablePriceTypes" :key="opt.code"
                        class="price-card" :class="{ active: form.price_type_code === opt.code }"
                        @click="selectAvailablePriceType(opt)">
                        <span class="price-card__name">{{ opt.name }}</span>
                        <span class="price-card__price">{{ monedaSymbol }} {{ opt.price.toFixed(2) }}</span>
                        <span v-if="opt.is_default" class="price-card__badge">Por defecto</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="col-md-12" v-if="calculatedTariff !== null">
                  <div class="tariff-info">
                    Tarifa calculada: {{ calculatedTariffLabel }} = {{ monedaSymbol }} {{ calculatedTariff.toFixed(2) }}
                  </div>
                </div>

              </div>

              <!-- ═══════════ SECCIÓN 5: Fecha y vuelo ═══════════ -->
              <div>
                <h2 class="form-subtitle">Fecha y vuelo</h2>
              </div>
              <div class="row">

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="fecha">Fecha *</label>
                    <FormInput id="fecha" type="text" placeholder="DD/MM/AAAA" v-model="form.fecha"
                      v-mask="'##/##/####'"
                      :show-error="$v.form.fecha.$error && !$v.form.fecha.required" />
                    <FormError v-if="$v.form.fecha.$error && !$v.form.fecha.required"
                      message="La fecha es requerida" />
                    <span v-if="isPastDate" class="past-date-warning">Estas registrando un viaje con fecha pasada</span>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="hora">Hora *</label>
                    <FormInput id="hora" type="text" placeholder="HH:MM" v-model="form.hora"
                      v-mask="'##:##'"
                      :show-error="$v.form.hora.$error && !$v.form.hora.required" />
                    <FormError v-if="$v.form.hora.$error && !$v.form.hora.required"
                      message="La hora es requerida" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="tipo_vuelo">Tipo de vuelo</label>
                    <FormSelect id="tipo_vuelo" v-model="form.tipo_vuelo" :items="tipoVueloOptions"
                      defaultOption="Elegir tipo de vuelo" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="num_vuelo">Numero de vuelo</label>
                    <FormInput id="num_vuelo" type="text" v-model.trim="form.num_vuelo" autocomplete="off"
                      placeholder="Ej: LA2045" />
                  </div>
                </div>

              </div>

              <!-- ═══════════ SECCIÓN 6: Identificación ═══════════ -->
              <div>
                <h2 class="form-subtitle">Identificacion</h2>
              </div>
              <div class="row">

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="ride">Ride <span v-if="!isOtaFuente" class="text-muted">(opcional)</span><span v-else> *</span></label>
                    <FormInput id="ride" type="text" v-model.trim="form.ride" autocomplete="off"
                      placeholder="Ej: POL-24512 (vacio = auto)"
                      :show-error="$v.form.ride.$error" />
                    <FormError v-if="$v.form.ride.$error"
                      message="El ride es obligatorio para esta fuente" />
                  </div>
                </div>

                <div class="col-md-4" v-if="!isOtaFuente">
                  <div class="form-group">
                    <label for="cupon_code">Cupon de descuento</label>
                    <FormInput id="cupon_code" type="text" v-model.trim="form.cupon_code" autocomplete="off"
                      placeholder="Ej: VERANO20" />
                    <span v-if="validatingCoupon" class="field-hint">Validando cupon...</span>
                    <span v-if="couponResult && couponResult.valid" class="field-hint field-hint--success">
                      Descuento: {{ couponResult.discount_type === 'percent' ? couponResult.discount_value + '%' : 'S/ ' + couponResult.discount_value }} (-S/ {{ couponResult.discount_amount.toFixed(2) }})
                    </span>
                    <span v-if="couponResult && !couponResult.valid" class="field-hint field-hint--error">
                      {{ couponResult.message }}
                    </span>
                  </div>
                </div>

                <!-- Desglose de precio con cupón -->
                <div class="col-md-12" v-if="!isOtaFuente && couponResult && couponResult.valid && form.precio">
                  <div class="price-breakdown">
                    <div class="price-breakdown__row">
                      <span>Precio original:</span>
                      <span>{{ monedaSymbol }} {{ parseFloat(form.precio).toFixed(2) }}</span>
                    </div>
                    <div class="price-breakdown__row price-breakdown__row--discount">
                      <span>Descuento ({{ couponResult.discount_type === 'percent' ? couponResult.discount_value + '%' : monedaSymbol + ' ' + couponResult.discount_value }} — {{ form.cupon_code }}):</span>
                      <span>-{{ monedaSymbol }} {{ parseFloat(form.descuento_aplicado).toFixed(2) }}</span>
                    </div>
                    <div class="price-breakdown__row price-breakdown__row--total">
                      <span>Precio final:</span>
                      <span>{{ monedaSymbol }} {{ computedPrecioFinal }}</span>
                    </div>
                  </div>
                </div>

                <div class="col-md-12">
                  <div class="form-group">
                    <label for="observaciones">Observaciones</label>
                    <textarea id="observaciones" v-model.trim="form.observaciones" rows="3"
                      placeholder="Ingresar observaciones" class="custom-form-control"></textarea>
                  </div>
                </div>

                <div class="col-md-12" v-if="editing && currentEstado === 'NON_SHOW'">
                  <div class="form-group">
                    <label for="motivo_non_show">Motivo Non Show</label>
                    <textarea id="motivo_non_show" v-model.trim="form.motivo_non_show" rows="2"
                      placeholder="Ingresar motivo del non show" class="custom-form-control"></textarea>
                  </div>
                </div>

              </div>

              <!-- Botones -->
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group-buttons text-right">
                    <router-link :to="{ name: 'viajes-ride-list' }" class="button button-light">
                      {{ editing ? "Volver" : "Cancelar" }}
                    </router-link>
                    <button class="button button-primary" type="submit">
                      Guardar
                    </button>
                  </div>
                </div>
              </div>

            </section>
          </b-card-body>
        </b-card>

      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormSelect, FormError, FormInput } from 'wize-admin'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { RideService, PassengerService, CouponService, BasePriceService, GeographyService } from '@/core/services'

// El aeropuerto va en el origen de los tipos APTO-* (APTO-HOT, APTO-DISTRITAL)
// y en el destino de los *-APTO (HOT-APTO, DISTRITAL-APTO). Mismo criterio que
// usa el backend para la tarifa del proveedor.
const isFromAirport = (tipo) => !!tipo && tipo.startsWith('APTO-')
const isToAirport = (tipo) => !!tipo && tipo.endsWith('-APTO')

// Segmentos NT-FS-006: Regular=1, Frecuente=4, VIP=6 (QORE y Corporativo eliminados).
const SEGMENT_CODE = { Regular: 1, Frecuente: 4, VIP: 6 }
const SEGMENT_LABEL = { 1: 'Regular', 4: 'Frecuente', 6: 'VIP' }

export default {
  components: { FormError, FormSelect, FormInput },
  data() {
    return {
      editing: false,
      loadingRide: false,
      // Aeropuerto de la ciudad activa (Jorge Chávez en Lima, Velasco Astete en
      // Cusco...). null si la ciudad no tiene uno activo.
      airport: null,
      // Búsqueda de pasajero
      searchingPhone: false,
      phoneSearched: false,
      passengerFound: false,
      passengerDetected: null,
      passengerTypeDetected: false,
      bookingHistory: null,
      phoneDebounceTimer: null,
      // Cupón
      validatingCoupon: false,
      couponResult: null,
      couponDebounceTimer: null,
      // Precio calculado (calculate-price)
      calculatingPrice: false,
      calculatedTariff: null,
      calculatedTariffLabel: '',
      availablePriceTypes: [],
      // Distritos
      districts: [],
      // Fuentes dinámicas
      bookingSources: { fixed: [], otas: [], affiliates: [] },
      // Opciones
      tipoViajeOptions: [
        { key: 'APTO-HOT',  label: 'APTO-HOT' },
        { key: 'HOT-APTO',  label: 'HOT-APTO' },
        { key: 'DISTRITAL', label: 'DISTRITAL' }
      ],
      tipoVueloOptions: [
        { key: 'NACIONAL', label: 'Nacional' },
        { key: 'INTER',    label: 'Internacional' }
      ],
      tipoVehiculoOptions: [
        { key: 'SEDAN',    label: 'Sedan' },
        { key: 'SUV',      label: 'SUV' },
        { key: 'VAN',      label: 'Van' },
        { key: 'SPRINTER', label: 'Sprinter' }
      ],
      monedaOptions: [
        { key: 'SOLES',   label: 'Soles (PEN)' },
        { key: 'DOLARES', label: 'Dolares (USD)' }
      ],
      tipoPasajeroOptions: [
        { key: 'Regular',   label: 'Regular' },
        { key: 'Frecuente', label: 'Frecuente' },
        { key: 'VIP',       label: 'VIP' }
      ],
      channelOptions: [
        { key: 'WHATSAPP',   label: 'WhatsApp' },
        { key: 'FACEBOOK',   label: 'Facebook' },
        { key: 'INSTAGRAM',  label: 'Instagram' },
        { key: 'EMAIL',      label: 'Correo electrónico' },
        { key: 'PHONE',      label: 'Teléfono' },
        { key: 'WEB',        label: 'Página web' },
        { key: 'PLATAFORMA', label: 'Plataforma' }
      ],
      form: {
        ride:              null,
        fecha:             null,
        hora:              null,
        fuente:            null,
        tipo_viaje:        null,
        origen:            null,
        destino:           null,
        tipo_vuelo:        null,
        num_vuelo:         null,
        observaciones:     null,
        nombres:           null,
        apellidos:         null,
        celular:           null,
        cant_pax:          null,
        cant_maletas:      null,
        tipo_vehiculo:     null,
        precio:            null,
        moneda:            null,
        distrito_origen:   null,
        distrito_destino:  null,
        tipo_pasajero:     null,
        cupon_code:        null,
        descuento_aplicado: null,
        precio_final:      null,
        auto_registrado:   false,
        source_type:       'DIRECT',
        ota_id:            null,
        affiliate_id:      null,
        channel:           null,
        country_code:      null,
        save_passenger:    true,
        passenger_price_type_id: null,
        motivo_non_show:   null,
        price_type_code:   null
      },
      currentEstado:       null
    }
  },
  validations: {
    form: {
      ride:          { required: requiredIf(function () { return this.isOtaFuente }) },
      fecha:         { required },
      hora:          { required },
      fuente:        { required },
      tipo_viaje:    { required },
      origen:        { required },
      destino:       { required },
      nombres:       { required },
      apellidos:     { required },
      celular:       { required },
      cant_pax:      { required },
      cant_maletas:  { required },
      tipo_vehiculo: { required },
      precio:        { required },
      moneda:        { required },
      // Obligatorio solo cuando el campo se muestra; si no, un viaje de OTA no
      // se podria guardar por un campo que el operador no ve.
      tipo_pasajero: { required: requiredIf(function () { return this.isDirectFuente }) }
    }
  },
  computed: {
    isPastDate() {
      if (this.editing || !this.form.fecha) return false
      const parts = this.form.fecha.split('/')
      if (parts.length !== 3 || this.form.fecha.length !== 10) return false
      const [day, month, year] = parts.map(Number)
      const selected = new Date(year, month - 1, day)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return selected < today
    },
    districtOptions() {
      return this.districts.map(d => ({ key: d.name, label: d.name }))
    },
    // El lado aeropuerto se bloquea solo si la ciudad tiene aeropuerto: sin uno
    // configurado, el operador tiene que poder escribirlo.
    origenReadonly() {
      return isFromAirport(this.form.tipo_viaje) && !!this.airport
    },
    destinoReadonly() {
      return isToAirport(this.form.tipo_viaje) && !!this.airport
    },
    distritoOrigenReadonly() {
      return this.origenReadonly
    },
    distritoDestinoReadonly() {
      return this.destinoReadonly
    },
    passengerTypeLabel() {
      return this.form.tipo_pasajero || ''
    },
    isOtaFuente() {
      return this.form.source_type === 'OTA'
    },
    // El segmento solo aplica a clientes directos (DEF-ADM-027): decide si el
    // campo se muestra y si es obligatorio.
    isDirectFuente() {
      return this.form.source_type === 'DIRECT'
    },
    monedaSymbol() {
      return this.form.moneda === 'DOLARES' ? 'US$' : 'S/'
    },
    computedPrecioFinal() {
      const precio = parseFloat(this.form.precio) || 0
      const descuento = parseFloat(this.form.descuento_aplicado) || 0
      return Math.max(0, precio - descuento).toFixed(2)
    },
    relevantDistrict() {
      if (this.form.tipo_viaje === 'HOT-APTO' || this.form.tipo_viaje === 'DISTRITAL') {
        return this.form.distrito_origen
      }
      if (this.form.tipo_viaje === 'APTO-HOT') {
        return this.form.distrito_destino
      }
      return null
    },
    relevantDistrictId() {
      const name = this.relevantDistrict
      if (!name) return null
      const d = this.districts.find(x => x.name === name)
      return d ? d.id : null
    }
  },
  watch: {
    '$route.params.id': function () {
      if (this.$route.params.id) this.load(this.$route.params.id)
    },
    'form.celular': function (val) {
      if (this.editing) return
      clearTimeout(this.phoneDebounceTimer)
      this.passengerFound = false
      this.passengerDetected = null
      this.passengerTypeDetected = false
      this.bookingHistory = null
      this.phoneSearched = false
      if (!val || val.length < 9) return
      this.phoneDebounceTimer = setTimeout(() => {
        this.searchPassenger(val)
      }, 400)
    },
    // Al elegir un tipo de aeropuerto se completa el aeropuerto de la ciudad
    // activa. Al cargar un viaje para editar no se toca: se respeta lo guardado.
    //
    // El distrito del lado aeropuerto queda vacío a propósito: antes iba 'Callao'
    // fijo, que es un distrito de Lima y quedaba guardado también en los viajes
    // de Cusco. Los aeropuertos no tienen distrito propio para reemplazarlo, ese
    // dato no entra en el precio ni en la tarifa del proveedor (usan el otro
    // extremo), y los viajes de Emma y las OTAs ya lo dejan vacío.
    'form.tipo_viaje': function (val) {
      if (!this.loadingRide) {
        const airportName = this.airport ? this.airport.name : null
        if (isFromAirport(val)) {
          this.form.origen = airportName
          this.form.distrito_origen = null
          this.form.destino = this.editing ? this.form.destino : null
          this.form.distrito_destino = this.editing ? this.form.distrito_destino : null
        } else if (isToAirport(val)) {
          this.form.destino = airportName
          this.form.distrito_destino = null
          this.form.origen = this.editing ? this.form.origen : null
          this.form.distrito_origen = this.editing ? this.form.distrito_origen : null
        }
      }
      this.calculatePrice()
    },
    'form.tipo_vehiculo': function () {
      this.calculatePrice()
    },
    'form.tipo_pasajero': function () {
      if (this.form.source_type === 'DIRECT') this.calculatePrice()
    },
    'form.ota_id': function (val) {
      const ota = this.bookingSources.otas.find(o => o.id === val)
      this.form.fuente = ota ? ota.value : null
    },
    'form.affiliate_id': function (val) {
      const aff = this.bookingSources.affiliates.find(a => a.id === val)
      if (aff) this.form.fuente = aff.value
      if (this.form.source_type === 'AFFILIATE') this.calculatePrice()
    },
    relevantDistrictId: function () {
      this.calculatePrice()
    },
    'form.cupon_code': function (val) {
      clearTimeout(this.couponDebounceTimer)
      this.couponResult = null
      if (!val || val.length < 3) {
        this.form.descuento_aplicado = null
        this.form.precio_final = null
        return
      }
      this.couponDebounceTimer = setTimeout(() => {
        this.validateCoupon(val)
      }, 400)
    }
  },
  methods: {
    // ── Sección 1: fuente ───────────────────────────────────────────
    selectSourceType(type) {
      this.form.source_type = type
      if (type === 'OTA') {
        this.form.ota_id = null
        this.form.affiliate_id = null
        this.form.tipo_pasajero = null
        this.form.channel = 'PLATAFORMA'
        this.form.moneda = 'DOLARES'
        this.form.save_passenger = false
        this.form.cupon_code = null
        this.form.fuente = null
        this.form.precio = null
        this.form.price_type_code = null
        this.availablePriceTypes = []
        this.calculatedTariff = null
        this.calculatedTariffLabel = ''
      } else if (type === 'AFFILIATE') {
        this.form.ota_id = null
        this.form.affiliate_id = null // el operador debe elegir
        this.form.tipo_pasajero = null
        if (this.form.channel === 'PLATAFORMA') this.form.channel = null
        this.form.moneda = 'SOLES'
        this.form.save_passenger = true
        this.form.fuente = null
        this.form.precio = null
        this.availablePriceTypes = []
        this.calculatedTariff = null
        this.calculatedTariffLabel = ''
      } else { // DIRECT
        this.form.ota_id = null
        this.form.affiliate_id = null
        if (this.form.channel === 'PLATAFORMA') this.form.channel = null
        this.form.moneda = 'SOLES'
        this.form.save_passenger = true
        // DIRECTO reemplaza a OTRO (DEF-ADM-027). El segmento del cliente va
        // aparte, en tipo_pasajero, y el backend lo fuerza a Regular para las
        // otras dos fuentes.
        this.form.fuente = 'DIRECTO'
        this.availablePriceTypes = []
        this.calculatePrice()
      }
    },

    // ── CAMBIO 1: Buscar pasajero (endpoint /passengers/search) ──────
    async searchPassenger(phone) {
      if (!phone) return
      this.searchingPhone = true
      try {
        const response = await PassengerService.search(phone)
        const data = response.payload || {}
        this.phoneSearched = true
        this.passengerFound = !!data.found
        this.bookingHistory = data.booking_history || null

        if (data.found && data.passenger) {
          // a) Pasajero registrado → autocompletar y bloquear
          const p = data.passenger
          this.passengerDetected = p
          this.fillNames(p.full_name)
          const label = SEGMENT_LABEL[p.passenger_type]
          if (label) {
            this.form.tipo_pasajero = label
            this.passengerTypeDetected = true
          }
          this.form.passenger_price_type_id = p.price_type_id || null
          this.form.save_passenger = false
        } else if (data.booking_history) {
          // b) No registrado pero con historial → sugerir registrar
          this.passengerDetected = null
          const h = data.booking_history
          if (h.last_name) this.form.nombres = h.last_name
          if (h.last_apellidos) this.form.apellidos = h.last_apellidos
          this.form.save_passenger = false
        } else {
          // c) Sin historial → checkbox según fuente
          this.passengerDetected = null
          this.form.save_passenger = this.form.source_type === 'DIRECT'
        }

        // CAMBIO 3: país
        if (data.country_code) {
          this.form.country_code = data.country_code
        } else {
          await this.detectCountryFor(phone)
        }
      } catch (e) {
        console.error(e)
        this.phoneSearched = true
        this.passengerFound = false
        this.passengerDetected = null
        this.bookingHistory = null
        await this.detectCountryFor(phone)
      } finally {
        this.searchingPhone = false
      }
    },
    fillNames(fullName) {
      if (!fullName || fullName === '-') return
      const parts = fullName.split(' ')
      if (parts.length >= 2) {
        this.form.nombres = parts.slice(0, Math.ceil(parts.length / 2)).join(' ')
        this.form.apellidos = parts.slice(Math.ceil(parts.length / 2)).join(' ')
      } else {
        this.form.nombres = fullName
      }
    },
    async detectCountryFor(phone) {
      try {
        const resp = await PassengerService.detectCountry(phone)
        this.form.country_code = (resp.payload && resp.payload.country_code) || null
      } catch (e) {
        // detección de país es best-effort; no interrumpe el flujo
        this.form.country_code = null
      }
    },

    // ── CAMBIO 2: Precio automático (endpoint /rides/calculate-price) ─
    async calculatePrice() {
      if (this.loadingRide) return
      if (this.form.source_type === 'OTA') {
        // OTA → precio manual, no se calcula
        this.availablePriceTypes = []
        this.calculatedTariff = null
        this.calculatedTariffLabel = ''
        return
      }
      const districtId = this.relevantDistrictId
      if (!districtId || !this.form.tipo_vehiculo) return
      if (this.form.source_type === 'AFFILIATE' && !this.form.affiliate_id) {
        // esperar a que el operador elija el afiliado
        this.form.precio = null
        this.availablePriceTypes = []
        this.calculatedTariff = null
        this.calculatedTariffLabel = ''
        return
      }

      const routeType = isFromAirport(this.form.tipo_viaje) ? 'airport_to_district' : 'district_to_airport'
      const payload = {
        source_type: this.form.source_type,
        affiliate_id: this.form.affiliate_id || null,
        passenger_segment: SEGMENT_CODE[this.form.tipo_pasajero] || 1,
        passenger_price_type_id: this.form.passenger_price_type_id || null,
        district_id: districtId,
        route_type: routeType,
        vehicle_type: this.form.tipo_vehiculo
      }

      this.calculatingPrice = true
      try {
        const response = await RideService.calculatePrice(payload)
        const r = response.payload || {}
        if (r.is_manual) {
          this.availablePriceTypes = []
          this.calculatedTariff = null
          this.calculatedTariffLabel = ''
          return
        }
        this.availablePriceTypes = r.available_price_types || []
        if (this.availablePriceTypes.length > 0) {
          // Afiliado con listas → pre-seleccionar la default
          const def = this.availablePriceTypes.find(a => a.is_default) || this.availablePriceTypes[0]
          this.applyPriceOption(def)
        } else {
          // Directo / afiliado sin listas → un solo precio del server
          this.form.price_type_code = r.price_type_code
          this.form.precio = r.price
          this.calculatedTariff = r.price
          this.calculatedTariffLabel = r.price_type_name
        }
      } catch (e) {
        console.error(e)
        this.form.precio = null
        this.availablePriceTypes = []
        this.calculatedTariff = null
        this.calculatedTariffLabel = ''
      } finally {
        this.calculatingPrice = false
      }
    },
    applyPriceOption(opt) {
      if (!opt) return
      this.form.price_type_code = opt.code
      this.form.precio = opt.price
      this.calculatedTariff = opt.price
      this.calculatedTariffLabel = opt.name
    },
    // ── CAMBIO 7: seleccionar una lista del afiliado ────────────────
    selectAvailablePriceType(opt) {
      this.applyPriceOption(opt)
    },

    // ── Validar cupón ───────────────────────────────────────────────
    async validateCoupon(code) {
      this.validatingCoupon = true
      try {
        const amount = this.form.precio || 0
        const response = await CouponService.validate(code, amount)
        this.couponResult = response.payload
        if (this.couponResult && this.couponResult.valid) {
          this.form.descuento_aplicado = this.couponResult.discount_amount
          this.form.precio_final = Math.max(0, (this.form.precio || 0) - this.couponResult.discount_amount)
        }
      } catch {
        this.couponResult = { valid: false, message: 'Error al validar el cupon' }
      } finally {
        this.validatingCoupon = false
      }
    },

    // ── Guardar ─────────────────────────────────────────────────────
    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$nextTick(() => {
          const el = document.querySelector('.form-error')
          if (el) {
            this.$smoothScroll({ scrollTo: el, updateHistory: false, offset: -50 })
          }
        })
        return
      }

      try {
        await this.$store.dispatch('app/loading', true)
        const data = { ...this.form }

        // Convertir fecha de DD/MM/YYYY a YYYY-MM-DD para el backend
        if (data.fecha && data.fecha.length === 10) {
          const [day, month, year] = data.fecha.split('/')
          data.fecha = `${year}-${month}-${day}`
        }

        if (!this.editing) {
          await RideService.create(data)
          Alert.success('Se almaceno la informacion correctamente')
          this.$router.push({ name: 'viajes-ride-list' })
        } else {
          await RideService.update(this.$route.params.id, data)
          Alert.success('Se almaceno la informacion correctamente')
          await this.load(this.$route.params.id)
        }
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    // ── Cargar para edición ─────────────────────────────────────────
    async load(id) {
      this.loadingRide = true
      try {
        await this.$store.dispatch('app/loading', true)
        const response = await RideService.getById(id)
        const p = response.payload
        this.editing = true

        this.form = {
          ride:               p.ride,
          fecha:              p.fecha ? p.fecha.substring(0, 10).split('-').reverse().join('/') : null,
          hora:               p.hora ? p.hora.substring(0, 5) : null,
          fuente:             p.fuente,
          tipo_viaje:         p.tipo_viaje,
          origen:             p.origen,
          destino:            p.destino,
          tipo_vuelo:         p.tipo_vuelo || null,
          num_vuelo:          p.num_vuelo || null,
          observaciones:      p.observaciones || null,
          nombres:            p.nombres,
          apellidos:          p.apellidos,
          celular:            p.celular,
          cant_pax:           p.cant_pax,
          cant_maletas:       p.cant_maletas,
          tipo_vehiculo:      p.tipo_vehiculo,
          precio:             parseFloat(p.precio),
          moneda:             p.moneda,
          distrito_origen:    p.distrito_origen || null,
          distrito_destino:   p.distrito_destino || null,
          tipo_pasajero:      p.tipo_pasajero || null,
          cupon_code:         p.cupon_code || null,
          descuento_aplicado: p.descuento_aplicado ? parseFloat(p.descuento_aplicado) : null,
          precio_final:       p.precio_final ? parseFloat(p.precio_final) : null,
          auto_registrado:    p.auto_registrado || false,
          source_type:        p.source_type || 'DIRECT',
          ota_id:             p.ota_id || null,
          affiliate_id:       p.affiliate_id || null,
          channel:            p.channel || null,
          country_code:       p.country_code || null,
          save_passenger:     false,
          passenger_price_type_id: p.passenger_price_type_id || null,
          motivo_non_show:    p.motivo_non_show || null,
          price_type_code:    p.price_type_code || null
        }
        this.currentEstado = p.estado || null
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar')
        this.$router.push({ name: 'viajes-ride-list' })
      } finally {
        await this.$store.dispatch('app/loading', false)
        this.$nextTick(() => { this.loadingRide = false })
      }
    },

    // ── Fuentes dinámicas ──────────────────────────────────────────
    async loadBookingSources() {
      try {
        const response = await RideService.getBookingSources()
        this.bookingSources = response.payload || { fixed: [], otas: [], affiliates: [] }
      } catch {
        this.bookingSources = { fixed: [], otas: [], affiliates: [] }
      }
    },

    // ── Cargar distritos ────────────────────────────────────────────
    // Aeropuerto de la ciudad activa. /airports devuelve los de todas las
    // ciudades (son pocos), así que se filtra aquí por la ciudad del header.
    async loadAirport() {
      const city = this.$store.getters['city/getCurrentCity']
      if (!city) {
        this.airport = null
        return
      }
      try {
        const response = await GeographyService.listAirports()
        const airports = response.payload || []
        this.airport = airports.find(a => a.city_id === city.id && a.is_active) || null
      } catch (error) {
        console.error('Error cargando el aeropuerto de la ciudad:', error)
        this.airport = null
      }
    },
    async loadDistricts() {
      try {
        const response = await BasePriceService.getDistricts()
        this.districts = response.payload || []
      } catch {
        this.districts = []
      }
    }
  },
  async created() {
    await Promise.all([this.loadDistricts(), this.loadBookingSources(), this.loadAirport()])
    if (this.$route.params.id) {
      await this.load(this.$route.params.id)
    }
  }
}
</script>

<style lang="stylus" scoped>
.past-date-warning
  display block
  margin-top 4px
  color #e67e22
  font-size 13px
  font-weight normal

.field-hint
  display block
  margin-top 4px
  font-size 12px
  color #6c757d

  &--success
    color #27ae60

  &--muted
    color #95a5a6

  &--error
    color #e74c3c

.tariff-info
  background #f0f7ff
  border-left 3px solid #3498db
  padding 8px 12px
  margin-bottom 16px
  font-size 13px
  color #2c3e50
  border-radius 0 4px 4px 0

.price-breakdown
  background #f8fff8
  border-left 3px solid #27ae60
  padding 10px 14px
  margin-bottom 16px
  border-radius 0 4px 4px 0
  &__row
    display flex
    justify-content space-between
    font-size 13px
    padding 2px 0
    &--discount
      color #e74c3c
    &--total
      font-weight bold
      font-size 14px
      border-top 1px solid #ddd
      padding-top 6px
      margin-top 4px
      color #27ae60

select.is-error
  border-color #dc3545

.checkbox-label
  display flex
  align-items center
  gap 8px
  font-weight normal
  cursor pointer
  input[type="checkbox"]
    margin 0

.source-type-group
  display flex
  gap 8px
  margin-bottom 4px

.source-btn
  flex 1
  padding 10px 16px
  border 1px solid #ccd1d9
  background #fff
  border-radius 6px
  font-size 14px
  font-weight 600
  color #6c757d
  cursor pointer
  transition all .15s ease
  &:hover
    border-color #3498db
    color #3498db
  &.active
    border-color #3498db
    background #3498db
    color #fff

.phone-input-group
  display flex
  gap 8px
  align-items flex-start
  .phone-search-btn
    white-space nowrap
    flex-shrink 0

.source-banner
  padding 10px 14px
  border-radius 6px
  font-size 13px
  font-weight 600
  margin-bottom 16px
  &--ota
    background #fff8e1
    border-left 3px solid #f39c12
    color #b9770e
  &--auto
    background #eafaf1
    border-left 3px solid #27ae60
    color #1e8449
  &--history
    background #eef4ff
    border-left 3px solid #3498db
    color #2c5aa0

.price-cards
  display flex
  gap 12px
  flex-wrap wrap
  margin-top 4px

.price-card
  position relative
  flex 1 1 160px
  min-width 160px
  padding 12px 14px
  border 1px solid #ccd1d9
  border-radius 8px
  background #fff
  cursor pointer
  text-align left
  transition all .15s ease
  display flex
  flex-direction column
  gap 4px
  &:hover
    border-color #3498db
  &.active
    border-color #3498db
    background #f0f7ff
  &__name
    font-weight 600
    font-size 14px
    color #2c3e50
  &__price
    font-size 16px
    font-weight 700
    color #27ae60
  &__badge
    font-size 11px
    color #3498db
    font-weight 600
</style>
